/**
 * TaskIOEditFields
 *
 * Custom inline-EDIT fields for the IO Request & Campaign Setup type, used by
 * TaskRequestTypeDetailsEditable (registered as the IO type's `EditFields`). Laid out
 * in the `.task-rtd-edit-grid` (label-above-control, four columns) so it mirrors the
 * read view. Number of Months stays read-only + auto-derived from the flight range,
 * and the money fields switch on the carried `dealType` (Channel Partner vs Advertiser).
 * `dealType` itself is not user-editable here, but it IS registered as a hidden field:
 * `Form.useWatch` only sees REGISTERED fields, so without it the watch read undefined and
 * every deal fell back to the Direct layout (issue #11760).
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, InputNumber, DatePicker } from 'antd';
import { monthsFromFlightRange } from './TaskIORequestFields';
import { currencyProps } from './fieldHelpers';
import MentionTextArea from '../../shared/MentionTextArea';

const { RangePicker } = DatePicker;

// One grid cell: label above an inline control.
const Field = ({ label, name, full, children }) => (
  <div className={`task-rtd-edit-grid__field${full ? ' task-rtd-edit-grid__field--full' : ''}`}>
    <span className="task-rtd-edit-grid__label">{label}</span>
    <Form.Item name={name} noStyle>
      {children}
    </Form.Item>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  full: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

Field.defaultProps = { full: false };

const TaskIOEditFields = ({ form }) => {
  const flightDates = Form.useWatch('flightDates', form);
  const isCP = Form.useWatch('dealType', form) === 'CHANNEL_PARTNER';

  useEffect(() => {
    form.setFieldsValue({ numberOfMonths: monthsFromFlightRange(flightDates) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightDates]);

  return (
    <div className="task-rtd-edit-grid">
      {/* Carried on the form from the saved details — registered so the watch above resolves it
          and the Save re-serializes into the same deal type's money fields. */}
      <Form.Item name="dealType" hidden>
        <Input />
      </Form.Item>

      <Field label="Flight Dates" name="flightDates">
        <RangePicker className="custom-date" dropdownClassName="task-date-dropdown" />
      </Field>
      <Field label="Number of Months" name="numberOfMonths">
        <InputNumber readOnly min={0} className="custom-input" />
      </Field>

      {isCP ? (
        <>
          <Field label="Channel Partner CPM" name="partnerCpm">
            <InputNumber {...currencyProps} className="custom-input" />
          </Field>
          <Field label="Channel Partner Budget" name="partnerTotalBudget">
            <InputNumber {...currencyProps} className="custom-input" />
          </Field>
          <Field label="Advertiser CPM" name="advertiserCpm">
            <InputNumber {...currencyProps} className="custom-input" />
          </Field>
        </>
      ) : (
        <>
          <Field label="CPM" name="cpm">
            <InputNumber {...currencyProps} className="custom-input" />
          </Field>
          <Field label="Total Budget" name="totalBudget">
            <InputNumber {...currencyProps} className="custom-input" />
          </Field>
        </>
      )}

      <Field label="Website" name="website" full>
        <Input className="custom-input" placeholder="https://example.com" />
      </Field>
      <Field label="Clickthrough URL" name="clickthroughUrl" full>
        <Input className="custom-input" placeholder="https://example.com/landing" />
      </Field>

      <Field label="Note" name="note" full>
        <MentionTextArea
          autoSize={{ minRows: 2, maxRows: 8 }}
          className="custom-textarea"
          placeholder="Add a note…"
        />
      </Field>
    </div>
  );
};

TaskIOEditFields.propTypes = { form: PropTypes.object.isRequired };

export default TaskIOEditFields;
